import { computed, inject } from '@angular/core';
import { filter, pipe, switchMap, tap } from 'rxjs';
import {
	signalStore,
	withState,
	withMethods,
	patchState,
	withComputed,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import {
	CheckboxOption,
	Offset,
	ProductEnum,
	Product,
	ClearingUniversities,
} from '@uc/web/shared/data-models';
import { DEFAULT_UNIVERSITY_SEARCH_PAYLOAD } from '../../static/universities.properties';
import { FilterApiService } from '@uc/web/shared/feature/feature-filter';
import { UniversitiesApiService } from '../universities-api.service';
import { generatePayload } from '../../utils/payload.utils';
import { generatePills } from '../../utils/pills.utils';
import { updateLocationFilter } from '../../utils/filter-options.utils';
import { updateQueryParams } from '../../utils/query-params.utils';
import {
	Universities,
	UniversitiesFilterOptions,
	UniversityFilterPill,
	UniversitySearchPayload,
	UniversitySearchQueryParamKey,
	UniversitySearchQueryParams,
} from '../../models/university-search.models';

type UniversityState = {
	queryParams: UniversitySearchQueryParams;
	payload: UniversitySearchPayload;
	product: Product;

	universities: Universities | null;
	clearingUniversities: ClearingUniversities | null;
	filters: UniversitiesFilterOptions;
	pills: UniversityFilterPill[];

	isLoading: boolean;
	isLoadingPagination: boolean;
	locationFiltersLoaded: boolean;
	error: string | null;
};

const initialState: UniversityState = {
	queryParams: {} as UniversitySearchQueryParams,
	payload: { ...DEFAULT_UNIVERSITY_SEARCH_PAYLOAD },
	product: ProductEnum.Undergraduate,

	universities: null,
	clearingUniversities: null,
	filters: {
		locations: [],
	},
	pills: [],

	isLoading: false,
	isLoadingPagination: false,
	locationFiltersLoaded: false,
	error: null,
};

export const UniversitiesStore = signalStore(
	{ protectedState: false },
	withState(initialState),

	withComputed((store) => ({
		filtersLoaded: computed(() => store.locationFiltersLoaded()),
	})),

	withMethods(
		(
			store,
			uniSrv = inject(UniversitiesApiService),
			filterApiSrv = inject(FilterApiService),
		) => ({
			fetchUniversities: rxMethod<void>(
				pipe(
					tap(() => patchState(store, { isLoading: true, error: null })),
					switchMap(() => {
						return uniSrv.getUniversities(store.payload()).pipe(
							tapResponse({
								next: (universities) =>
									patchState(store, {
										isLoading: false,
										isLoadingPagination: false,
										universities,
									}),

								error: (error: string) => {
									console.error('error', error);
									patchState(store, { error: error });
								},
								finalize: () =>
									patchState(store, {
										isLoading: false,
										isLoadingPagination: false,
									}),
							}),
						);
					}),
				),
			),

			fetchPaginatedUniversities: rxMethod<void>(
				pipe(
					tap(() => patchState(store, { isLoading: true, error: null })),
					switchMap(() => {
						return uniSrv.getUniversities(store.payload()).pipe(
							tapResponse({
								next: (universities) => {
									const data = [
										...(store.universities()?.data || []),
										...universities.data,
									];

									patchState(store, {
										isLoading: false,
										isLoadingPagination: false,
										universities: {
											...universities,
											data,
										},
									});
								},
								error: (error: string) =>
									patchState(store, { error: error }),
								finalize: () =>
									patchState(store, {
										isLoading: false,
										isLoadingPagination: false,
									}),
							}),
						);
					}),
				),
			),

			fetchClearingUniversities: rxMethod<void>(
				pipe(
					tap(() => patchState(store, { isLoading: true, error: null })),
					switchMap(() => {
						return uniSrv.getClearingUniversities(store.payload()).pipe(
							tapResponse({
								next: (clearingUniversities) => {
									patchState(store, {
										isLoading: false,
										isLoadingPagination: false,
										clearingUniversities,
									});
								},
								error: (error: string) =>
									patchState(store, { error: error }),
								finalize: () =>
									patchState(store, {
										isLoading: false,
										isLoadingPagination: false,
									}),
							}),
						);
					}),
				),
			),

			fetchPaginatedClearingUniversities: rxMethod<void>(
				pipe(
					tap(() => patchState(store, { isLoading: true, error: null })),
					switchMap(() => {
						return uniSrv.getClearingUniversities(store.payload()).pipe(
							tapResponse({
								next: (clearingUniversities) => {
									// when user clicks on load more
									const data = [
										...(store.clearingUniversities()?.data || []),
										...clearingUniversities.data,
									];

									patchState(store, {
										isLoading: false,
										isLoadingPagination: false,
										clearingUniversities: {
											...clearingUniversities,
											data,
										},
									});
								},
								error: (error: string) =>
									patchState(store, { error: error }),
								finalize: () =>
									patchState(store, {
										isLoading: false,
										isLoadingPagination: false,
									}),
							}),
						);
					}),
				),
			),

			fetchFilterLocations: rxMethod<void>(
				pipe(
					filter(() => !store.locationFiltersLoaded()),
					switchMap(() => filterApiSrv.getLocations()),
					tap((locations) =>
						patchState(store, {
							filters: { ...store.filters(), locations },
							locationFiltersLoaded: true,
						}),
					),
				),
			),

			initQueryParamsAndProduct(
				queryParams: UniversitySearchQueryParams,
				product: Product,
			) {
				let mergedQueryParams = {
					...store.queryParams(),
					...queryParams,
				};

				const data = formatStateData(
					store.payload(),
					mergedQueryParams,
					store.filters(),
					false,
				);

				// Update page if query params on page refresh or accessed with URL
				const page = data.payload.page ?? 1;
				if (page > 1) {
					mergedQueryParams = {
						...mergedQueryParams,
						page: page.toString(),
					};
				}

				const payloadProduct =
					product === ProductEnum.Postgraduate
						? 'POSTGRADUATE'
						: 'UNDERGRADUATE';

				patchState(store, {
					queryParams: mergedQueryParams,
					payload: { ...data.payload, product: payloadProduct },
					filters: data.filters,
					pills: data.pills,
					isLoading: data.isLoading,
					product: product,
				});

				this.fetchUniversitiesByProduct(store.product());
			},

			updateQueryParams(
				urlKey: UniversitySearchQueryParamKey,
				urlValue: string,
				isChecked: boolean,
			) {
				const updatedQueryParams = updateQueryParams(
					store.queryParams(),
					urlKey,
					urlValue,
					isChecked,
				);
				const data = formatStateData(
					store.payload(),
					updatedQueryParams,
					store.filters(),
				);
				patchState(store, {
					queryParams: updatedQueryParams,
					payload: data.payload,
					filters: data.filters,
					pills: data.pills,
					isLoading: data.isLoading,
				});
				this.fetchUniversitiesByProduct(store.product());
			},

			resetQueryParams(fetchUniversities = true) {
				const updatedQueryParams = {} as UniversitySearchQueryParams;
				const data = formatStateData(
					store.payload(),
					updatedQueryParams,
					store.filters(),
				);
				patchState(store, {
					queryParams: updatedQueryParams,
					payload: data.payload,
					filters: data.filters,
					pills: data.pills,
					isLoading: data.isLoading,
				});

				if (fetchUniversities) {
					this.fetchUniversitiesByProduct(store.product());
				}
			},

			updateSearchTerm(searchTerm: string) {
				const updatedQueryParams = updateQueryParams(
					store.queryParams(),
					's',
					searchTerm,
				);
				const data = formatStateData(
					store.payload(),
					updatedQueryParams,
					store.filters(),
				);
				patchState(store, {
					queryParams: updatedQueryParams,
					payload: data.payload,
					filters: data.filters,
					pills: data.pills,
					isLoading: data.isLoading,
				});

				this.fetchUniversitiesByProduct(store.product());
			},

			updateUniversitySearchPage(page: number) {
				const integer = Number.isInteger(page) ? page : 1;

				patchState(store, {
					queryParams: {
						...store.queryParams(),
						page: integer.toString(),
					},
					payload: {
						...store.payload(),
						merge: true,
						offset: Offset.On,
						fromStart: false,
						page: integer,
					},
					isLoadingPagination: true,
				});

				this.fetchPaginatedUniversitiesByProduct(store.product());
			},

			updateProduct(product: Product) {
				const _product =
					product === 'postgraduate' ? 'POSTGRADUATE' : 'UNDERGRADUATE';
				patchState(store, {
					product,
					payload: { ...store.payload(), product: _product, page: 1 },
				});

				this.fetchUniversitiesByProduct(store.product());
			},

			resetStore() {
				patchState(store, {
					queryParams: {} as UniversitySearchQueryParams,
					payload: { ...DEFAULT_UNIVERSITY_SEARCH_PAYLOAD },
					universities: null,
					clearingUniversities: null,
					filters: resetFilters(store.filters()),
					pills: [],
				});
			},

			fetchUniversitiesByProduct(product: Product) {
				if (product === ProductEnum.Clearing) {
					this.fetchClearingUniversities();
				} else {
					this.fetchUniversities();
				}
			},

			fetchPaginatedUniversitiesByProduct(product: Product) {
				if (product === ProductEnum.Clearing) {
					this.fetchPaginatedClearingUniversities();
				} else {
					this.fetchPaginatedUniversities();
				}
			},
		}),
	),
);

const formatStateData = (
	payload: UniversitySearchPayload,
	queryParams: UniversitySearchQueryParams,
	filters: UniversitiesFilterOptions,
	resetPage = true,
) => {
	if (resetPage) delete queryParams.page;

	const updatedPayload = generatePayload(payload, queryParams, filters);

	//update checked values on all filters
	const updatedFilters = updateLocationFilter(filters, updatedPayload);

	const pills = generatePills(updatedPayload, updatedFilters);

	return {
		payload: updatedPayload,
		filters: updatedFilters,
		pills: pills,
		isLoading: true,
	};
};

const resetFilters = (filters: UniversitiesFilterOptions) => {
	Object.values(filters).forEach((filter: CheckboxOption[]) => {
		filter.forEach((option) => {
			option.checked = false;
		});
	});
	return filters;
};
